import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';
import { AttachmentService } from '../attachment.service';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from 'angular2-notifications';
import { QuillImagePasteService } from './quill-image-paste.service';
import { EmojiBlot } from '@sb-tools/typescript/services/emoji-blot';
import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { CustomBlockQuote } from '@sb-tools/typescript/services/quill/custom-blockquote';
import { isPlatformBrowser } from '@angular/common';
import Delta from 'quill-delta';

const FIlE_MAX_SIZE_25MB = 25 * 1000 * 1000;
// Use the numeric node type because this service is constructed before browser globals are always safe to reference.
const ELEMENT_NODE = 1;

@Injectable({
    providedIn: 'root',
})
export class QuillHelperService implements OnDestroy {
    editedReplyId = null;

    // Reply templates are authored in Summernote and inserted into Quill. These matchers preserve the inline styles
    // and image dimensions that Quill does not reliably keep with its default clipboard conversion.
    private quillClipboardConfig = {
        matchers: [
            [ELEMENT_NODE, this.preserveInlineStylesMatcher.bind(this)],
            ['img', this.preserveImageSizeMatcher.bind(this)],
        ],
    };

    htmlQuillConfig = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'], // toggled buttons

            [{ list: 'ordered' }, { list: 'bullet' }],

            [{ header: [1, 2, 3, 4, 5, 6, false] }],

            [{ color: [] }, { background: [] }], // dropdown with defaults from theme

            [{ align: [] }],

            ['clean', 'blockquote'], // remove formatting button

            // remove image controls on mobile devices because it doesnt work in safari ios
            ['link', ...(HelperService.isMobileOrTabletDevice() ? [] : ['image'])],
        ],
        imageDrop: {
            saveToServer: (quill, file) => {
                this.saveToServer(quill, file);
            },
        },
        imageResize: {
            displaySize: true,
        },
        clipboard: this.quillClipboardConfig,
    };

    textQuillConfig = {
        toolbar: false,
        imageDrop: {
            saveToServer: (quill, file) => {
                this.saveToServer(quill, file);
            },
        },
        imageResize: {
            displaySize: true,
        },
        clipboard: this.quillClipboardConfig,
    };

    customToolbarQuillConfig = {
        imageDrop: {
            saveToServer: (quill, file) => {
                this.saveToServer(quill, file);
            },
        },
        imageResize: {
            displaySize: true,
        },
        clipboard: this.quillClipboardConfig,
    };

    private unsubscriber = new Subject<void>();

    constructor(
        private attachmentService: AttachmentService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        @Inject(PLATFORM_ID) private platformId: string,
    ) {}

    checkQuillEmpty(quillEditor) {
        if (quillEditor) {
            if ((quillEditor.getContents()['ops'] || []).length !== 1) {
                return false;
            }
            return quillEditor.getText().trim().length === 0;
        }

        return null;
    }

    convertImageSizeFromStylesToAttributes(text) {
        const div = document.createElement('div');
        div.innerHTML = text;

        const images = Array.from(div.getElementsByTagName('img'));

        if (images.length) {
            images.forEach((image) => {
                if (!image.getAttribute('width') && image.style.width) {
                    image.setAttribute('width', image.style.width);
                }

                if (!image.getAttribute('height') && image.style.height) {
                    image.setAttribute('height', image.style.height);
                }
            });
        }
        return div.innerHTML;
    }

    getNoteQuillConfig(linebreakHandler) {
        return {
            ...this.customToolbarQuillConfig,
            keyboard: {
                bindings: {
                    linebreak: {
                        key: 'Enter',
                        shortKey: true, // cmd or ctrl key
                        handler: () => {
                            linebreakHandler();
                        },
                    },
                },
            },
        };
    }

    async initQuillRegisters(): Promise<void> {
        if (isPlatformBrowser(this.platformId)) {
            const QuillModule = await import('quill');
            const Quill = QuillModule.default;
            const Parchment = Quill.import('parchment') as any;
            const BaseImage = Quill.import('formats/image') as any;
            // Quill's default font style attributor has a small whitelist. Templates can contain fonts from
            // Summernote, so keep the actual font-family value and strip browser-added quotes.
            const FontStyleAttributor = class extends Parchment.StyleAttributor {
                constructor(...args: any[]) {
                    super(...args);
                }

                value(node: HTMLElement): string {
                    return super.value(node).replace(/["']/g, '');
                }
            };
            // Quill's image blot only preserves width/height attributes. Image resize can produce inline styles,
            // especially percentage widths like 100%, so read and write those styles explicitly.
            class ImageWithStyleSize extends BaseImage {
                static create(value: string): HTMLImageElement {
                    const node = super.create(value) as HTMLImageElement;

                    node.addEventListener('mousedown', () => {
                        const width = node.style.width;

                        if (!QuillHelperService.isPercentageImageDimension(width)) {
                            return;
                        }

                        node.setAttribute('width', `${Math.round(node.getBoundingClientRect().width)}`);
                        node.style.width = '';
                    });

                    return node;
                }

                static formats(domNode: HTMLImageElement): Record<string, string> {
                    const formats = super.formats(domNode);

                    if (domNode.style.width) {
                        formats.width = QuillHelperService.normalizeImageDimension(domNode.style.width);
                    }

                    if (domNode.style.height) {
                        formats.height = QuillHelperService.normalizeImageDimension(domNode.style.height);
                    }

                    return formats;
                }

                format(name: string, value: string): void {
                    if (name === 'width' || name === 'height') {
                        this.setImageDimension(name, value);
                        return;
                    }

                    super.format(name, value);
                }

                private setImageDimension(name: string, value: string): void {
                    const style = name === 'width' ? 'width' : 'height';

                    if (!value) {
                        this.domNode.removeAttribute(name);
                        this.domNode.style[style] = '';
                        return;
                    }

                    if (QuillHelperService.isCssImageDimension(value)) {
                        this.domNode.removeAttribute(name);
                        this.domNode.style[style] = value;
                        return;
                    }

                    this.domNode.setAttribute(name, value);
                    this.domNode.style[style] = '';
                }
            }
            const Block = Quill.import('blots/block') as any;
            Block.tagName = 'div';
            Quill.register(Block, true);
            // Use unrestricted style attributors so values from Summernote templates are not dropped on insert.
            const font = new FontStyleAttributor('font', 'font-family', {
                scope: Parchment.Scope.INLINE,
            });
            const lineHeight = new Parchment.StyleAttributor('lineHeight', 'line-height', {
                scope: Parchment.Scope.BLOCK,
            });
            const size = new Parchment.StyleAttributor('size', 'font-size', {
                scope: Parchment.Scope.INLINE,
            });
            const align = Quill.import('attributors/style/align') as any;
            Quill.register(align, true);
            Quill.register(Quill.import('attributors/style/background') as any, true);
            Quill.register(Quill.import('attributors/style/color') as any, true);
            Quill.register(Quill.import('attributors/style/direction') as any, true);
            Quill.register(font, true);
            Quill.register(lineHeight, true);
            Quill.register(size, true);
            Quill.register(ImageWithStyleSize, true);
            Quill.register('modules/imageDrop', QuillImagePasteService);
            Quill.register({ 'formats/emoji': EmojiBlot }, true);
            const ImageResizeModule = await import('quill-image-resize');
            const ImageResize =
                ImageResizeModule.ImageResize || ImageResizeModule.default?.default || ImageResizeModule.default;
            if (ImageResize) {
                Quill.register('modules/imageResize', ImageResize);
            }
            Quill.register(CustomBlockQuote);
        }
    }

    private preserveInlineStylesMatcher(node: HTMLElement, delta: Delta): Delta {
        // Also handle legacy attributes because pasted/template HTML can contain old <font> and bgcolor markup.
        const style = node.style;
        const formats = {
            ...(style.color || node.getAttribute('color') ? { color: style.color || node.getAttribute('color') } : {}),
            ...(style.backgroundColor || node.getAttribute('bgcolor')
                ? { background: style.backgroundColor || node.getAttribute('bgcolor') }
                : {}),
            ...(style.fontFamily || node.getAttribute('face')
                ? { font: (style.fontFamily || node.getAttribute('face')).replace(/["']/g, '') }
                : {}),
            ...(style.fontSize ? { size: style.fontSize } : {}),
            ...(style.lineHeight ? { lineHeight: style.lineHeight } : {}),
        };

        return this.applyFormats(delta, formats);
    }

    private preserveImageSizeMatcher(node: HTMLImageElement, delta: Delta): Delta {
        const width = node.style.width || node.getAttribute('width');
        const height = node.style.height || node.getAttribute('height');
        const formats = {
            ...(width ? { width: QuillHelperService.normalizeImageDimension(width) } : {}),
            ...(height && height !== 'auto' ? { height: QuillHelperService.normalizeImageDimension(height) } : {}),
        };

        return this.applyFormats(delta, formats);
    }

    private applyFormats(delta: Delta, formats: Record<string, string>): Delta {
        // Clipboard matchers receive a Delta from Quill. Rebuild it with our extra formats so Quill keeps
        // Summernote styles instead of stripping them during HTML-to-Delta conversion.
        if (!Object.keys(formats).length) {
            return delta;
        }

        return delta.reduce((newDelta, op) => {
            if (!op.insert) {
                return newDelta;
            }

            return newDelta.insert(op.insert, {
                ...formats,
                ...op.attributes,
            });
        }, new Delta());
    }

    private static normalizeImageDimension(value: string): string {
        // Quill stores pixel image dimensions as plain numbers, but CSS dimensions such as 100% must stay intact.
        const trimmedValue = value.trim();
        const pxValue = trimmedValue.match(/^(\d+(?:\.\d+)?)px$/);

        return pxValue ? pxValue[1] : trimmedValue;
    }

    private static isCssImageDimension(value: string): boolean {
        // Percentage/em/rem/etc. values cannot be safely written as width/height attributes, so keep them as styles.
        return /[%a-z]/i.test(value) && !/^\d+(?:\.\d+)?px$/i.test(value);
    }

    private static isPercentageImageDimension(value: string): boolean {
        return /^\d+(?:\.\d+)?%$/.test(value.trim());
    }

    removeResizeCursorFromImages(tmpString) {
        if (tmpString) {
            return tmpString.replace(
                /<img(.)*style=("|')(.)*(cursor: (nwse-resize)|(nesw-resize))(.)*("|')(.)*>$/gi,
                (fullMatch) => {
                    return fullMatch.replace(/cursor: ((nwse-resize)|(nesw-resize))/, '');
                },
            );
        } else {
            return tmpString;
        }
    }

    /**
     * Step1. select local image
     *
     */
    selectLocalImage(editor) {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        // Listen upload local image and save to server
        input.oninput = () => {
            const file = input.files[0];

            // file type is only image.
            if (/^image\//.test(file.type)) {
                this.saveToServer(editor, file);
            } else {
                console.warn('You could only upload images.');
            }
        };
    }

    saveAsAttachment(attachments, file: File) {
        if (this.isFileExceedLimit(file.size)) {
            return;
        }

        this.attachmentService
            .uploadTicketAttachment(file)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                attachments.push(data);
            });
    }

    saveToServer(editor, file: File) {
        if (this.isFileExceedLimit(file.size)) {
            return;
        }

        this.attachmentService
            .uploadFile(file)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                this.insertToEditor(editor, data.fileUrl);
            });
    }

    isFileExceedLimit(size) {
        if (size > FIlE_MAX_SIZE_25MB) {
            this.notificationsService.error(
                this.translateService.instant('Error!'),
                this.translateService.instant('File is too big. Max file size: 25MB.'),
            );
            return true;
        } else {
            return false;
        }
    }

    insertToEditor(editor, url: string) {
        // push image url to rich editor.
        const range = editor.getSelection();
        editor.insertEmbed(range.index, 'image', url, 'user');
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
